import { computed, makeObservable, observable, runInAction, action } from 'mobx';
import { Auth, BubbleDownCase, DatabusNotification, DeepActionResult } from '.';
import IntegrationModel from 'views/Integrations/IntegrationsModel';
import NotificationModel from 'views/NotificationLayer/NotificatonModel';
import { detectRoute } from './Browser';
import { isInRoute } from '../utils/isInRoute';
import { ConnectAuth } from './AuthModel';
import { useResourceMapperStore } from '../views/ResourceMapper/store/useResourceMapperStore';

class RootModel {
  static type = {
    AUTH_MODEL: 'auth',
    LAYOUT_MODEL: 'layout',
  };
  userId: string = '';
  userEmail: string = '';
  orgId: string = '';
  orgName: string = '';
  _activeUser: string = '';
  farmId: string = '';
  firstFarmId: string = '';
  unitSystem: string = '';
  private AuthStore: Auth;
  private IntegrationsModel: IntegrationModel;
  private NotificationStore: NotificationModel;

  constructor() {
    makeObservable(this, {
      userId: observable,
      userEmail: observable,
      orgId: observable,
      activeUser: computed,
      orgName: observable,
      unitSystem: observable,
      changeUnitSystem: action,
      setSelectedFarmId: action,
    });
    this.unitSystem = '';
    this.AuthStore = new Auth(this);
    this.IntegrationsModel = new IntegrationModel(this);
    this.NotificationStore = new NotificationModel(this);
  }
  get root() {
    return this;
  }
  get auth() {
    return this.AuthStore;
  }
  get integrations() {
    return this.IntegrationsModel;
  }
  get activeUser() {
    return this._activeUser;
  }
  get notifications() {
    return this.NotificationStore;
  }
  get deepAction() {
    return this.AuthStore.deepAction;
  }

  setSelectedFarmId(id: string) {
    if (id !== '0' || id) {
      this.farmId = id;
    }
  }

  setFirstFarmId(id: string) {
    runInAction(() => {
      this.firstFarmId = id;
    });
  }

  async setUserId(userId: string) {
    runInAction(() => {
      this.userId = userId;
    });
    if (this.orgId) await this.setActiveUser(userId, this.orgId);
  }

  async setOrgInformation(orgId: string, orgName: string, orgUnitSystem: string) {
    runInAction(() => {
      this.orgId = orgId;
      this.orgName = orgName;
      this.unitSystem = orgUnitSystem;
    });
    if (this.userId) await this.setActiveUser(this.userId, orgId);
  }

  changeUnitSystem(unit: string) {
    runInAction(() => {
      this.unitSystem = unit;
    });
  }

  async setActiveUser(userId: string, orgId: string) {
    const activeUser = `${userId}_${orgId}`;
    if (this.activeUser === activeUser) return;
    this.IntegrationsModel.setActiveUser(activeUser);
    await this.AuthStore.setActiveUser(activeUser);
    this.bubbleDown(BubbleDownCase.ORG_ID_CHANGE, userId, orgId, detectRoute());
    runInAction(() => {
      this._activeUser = activeUser;
    });
  }

  bubbleDown(func_name: BubbleDownCase, ...args: any[]) {
    //avoiding invoking resource mapper model methods when not in resource mapper route
    if (!isInRoute('resource-mapper')) return;
    switch (func_name) {
      case BubbleDownCase.ORG_ID_CHANGE: {
        useResourceMapperStore.getState().syncStore(BubbleDownCase.ORG_ID_CHANGE, ...args);
        break;
      }
      case BubbleDownCase.USER_ID_CHANGE: {
        useResourceMapperStore.getState().syncStore(BubbleDownCase.USER_ID_CHANGE, ...args);
        break;
      }
      default:
        break;
    }
  }

  getCropwiseAuthToken() {
    return this.AuthStore.token;
  }

  set_notification(note: DatabusNotification | null) {
    this.notifications.set_notification(note);
  }

  complete_deep_action(value: DeepActionResult, error_code?: string) {
    this.AuthStore.complete_deep_action(value, error_code);
  }

  setConnectAuth(connectAuth: ConnectAuth): void {
    this.AuthStore.setConnectAuth(connectAuth);
  }
}

export default RootModel;
